// OP-3605 — Nova transferência (ESL5): escolher origem, destino e o que vai.
// Peça serializada é escolhida uma a uma entre as DISPONÍVEIS da origem — é a
// mesma régua de `podeIncluirNaTransferencia`, que recusa peça fora da origem,
// já comprometida em outra transferência ou não disponível.
import { FC, useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useEstoqueDisponiveis, useEstoqueItensSolicitacao, useEstoqueLocais, useEstoqueOndeTem, useEstoqueSolicitacaoDetalhe } from '../../../../hooks/estoque'
import { usePerfilEstoque } from '../../../../hooks/estoque/perfil'
import { createEstoqueTransferencia } from '../../../../Service/estoque'
import { CatalogoItemRow, NovaTransferenciaItem } from '../../../../types/estoque'
import {
  BD, Button, Card, Field, Input, PageSubtitle, PageTitle, SEVERIDADE, Select, SoDesktop, Spinner, Table,
} from '../../styles'
import { BarraFixa } from '../BarraFixa'
import { tratarErro } from '../erros'
import { plural } from '../formato'
import { SeletorDePecas } from '../SeletorDePecas'
import { hojeISO } from '../Solicitar/form'
import { IMPEDIMENTO_FORA_DO_ESCOPO, impedimentoNovaTransferencia, impedimentoQuantidadeConsumivel, ladoDaCriacao, textoDoLado } from '../Transferencia/regras'
import { EscolhaDeOrigem, confirmarOrigem, linhasOndeTem, manterOrigem, pedirOrigem } from './regras'
import { LOCAL_EXECUTION } from '../../../../utils/featureFlag'

const LIMITE_PECAS = 200

interface LinhaNova extends NovaTransferenciaItem {
  /** Só para a tela: o que mostrar na lista do que vai. */
  rotulo: string
}

export const EstoqueNovaTransferencia: FC = () => {
  const navigate = useNavigate()
  const hoje = useMemo(hojeISO, [])
  const { pode } = usePerfilEstoque()
  const podeTransferir = pode('TRANSFERIR')
  const { locais, isLoading: carregandoLocais, isError: erroLocais, isUnauthorized, mutate: recarregarLocais } = useEstoqueLocais()
  const itensQ = useEstoqueItensSolicitacao()
  const [params] = useSearchParams()
  const numeroDe = (nome: string): number | null => (Number(params.get(nome)) > 0 ? Number(params.get(nome)) : null)
  const requisicaoId = numeroDe('requisicaoId')
  const pedidoQ = useEstoqueSolicitacaoDetalhe(requisicaoId)
  const [origemId, setOrigemId] = useState<number | null>(null)
  const [destinoId, setDestinoId] = useState<number | null>(() => (requisicaoId == null ? numeroDe('destinoId') : null))
  const [motivo, setMotivo] = useState('')
  const [previsao, setPrevisao] = useState('')
  const [itemId, setItemId] = useState<number | null>(() => numeroDe('itemId'))
  const [buscaPeca, setBuscaPeca] = useState('')
  const [quantidade, setQuantidade] = useState(() => (numeroDe('quantidade') ? String(numeroDe('quantidade')) : ''))
  const [linhas, setLinhas] = useState<LinhaNova[]>([])
  const [busy, setBusy] = useState(false)
  const [origemPendente, setOrigemPendente] = useState<{ id: number | null } | null>(null)
  const destinoDoPedido = pedidoQ.data ? Number(pedidoQ.data.local_origem_id) : null
  const itensDoPedidoChave = pedidoQ.data ? pedidoQ.data.itens.map((i) => i.itemId).join(',') : null

  useEffect(() => {
    if (destinoDoPedido == null) return
    setDestinoId(destinoDoPedido)
    const doPedido = String(itensDoPedidoChave ?? '').split(',').map(Number)
    setLinhas((ls) => ls.filter((l) => doPedido.indexOf(Number(l.itemId)) >= 0))
  }, [destinoDoPedido, itensDoPedidoChave])

  const item: CatalogoItemRow | undefined = itensQ.data.find((i) => i.id === itemId)
  const ehSerial = item?.controle === 'SERIAL'
  // Só as peças que o servidor aceitaria: em estoque, OK, ativas e na origem.
  const pecasQ = useEstoqueDisponiveis(ehSerial ? itemId : null, origemId, LIMITE_PECAS, buscaPeca, 'TRANSFERENCIA')
  const jaEscolhidas = linhas.filter((l) => l.unidadeId != null).map((l) => l.unidadeId)
  const ondeTemQ = useEstoqueOndeTem(item ? itemId : null)
  const saldoOrigem = origemId != null ? ondeTemQ.data.find((l) => Number(l.localId) === origemId) : undefined
  const jaIncluido = linhas.filter((l) => l.itemId === itemId && l.unidadeId == null).reduce((a, l) => a + (Number(l.quantidade) || 0), 0)
  const livre = ondeTemQ.isLoading || ondeTemQ.isError ? null : Math.max(0, (Number(saldoOrigem?.disponivel) || 0) - jaIncluido)

  if (isUnauthorized && !LOCAL_EXECUTION) return <Navigate to="/auth" replace />

  const noEscopo = (id: number | null) => id != null && locais.some((l) => l.id === id && l.noEscopo)
  const lado = ladoDaCriacao({ origemNoEscopo: noEscopo(origemId), destinoNoEscopo: noEscopo(destinoId), comPedido: requisicaoId != null })
  const impedimentoDoPedido = requisicaoId == null || pedidoQ.data ? null
    : pedidoQ.isLoading ? 'Carregando o pedido…' : 'Não foi possível carregar o pedido; a transferência vinculada depende dele.'
  const impedimento = impedimentoDoPedido ?? impedimentoNovaTransferencia(origemId, destinoId, linhas.length) ?? (lado ? null : IMPEDIMENTO_FORA_DO_ESCOPO)
  const impQuantidade = item && !ehSerial ? impedimentoQuantidadeConsumivel(quantidade, livre) : null
  const ondeTem = linhasOndeTem(ondeTemQ.data, origemId, destinoId, ehSerial)
  const nomeDoLocal = (id: number | null) => locais.find((l) => l.id === id)?.nome ?? 'nenhuma'
  const pedido = pedidoQ.data
  const itensDoPedido = pedido ? pedido.itens.map((i) => i.itemId) : null
  const itensOferecidos = itensDoPedido ? itensQ.data.filter((i) => itensDoPedido.indexOf(i.id) >= 0) : itensQ.data
  const avisoDoLado = origemId && destinoId ? textoDoLado(lado, nomeDoLocal(origemId), nomeDoLocal(destinoId)) : null

  const escolha: EscolhaDeOrigem<LinhaNova> = { origemId, linhas, pendente: origemPendente }
  const aplicarEscolha = (e: EscolhaDeOrigem<LinhaNova>) => {
    if (e.origemId !== origemId) setBuscaPeca('')
    setOrigemId(e.origemId); setLinhas(e.linhas); setOrigemPendente(e.pendente)
  }
  const escolherOrigem = (id: number | null) => aplicarEscolha(pedirOrigem(escolha, id))

  const incluirPeca = (unidadeId: number, serial: string) => {
    if (!itemId) return
    if (jaEscolhidas.indexOf(unidadeId) >= 0) return
    setLinhas((ls) => [...ls, { itemId, unidadeId, rotulo: `${item?.nome ?? 'Peça'} · ${serial}` }])
  }

  const incluirQuantidade = () => {
    if (!itemId || !item) return
    const n = Number(quantidade)
    if (!Number.isInteger(n) || n <= 0) { toast.error('Informe uma quantidade inteira maior que zero.'); return }
    setLinhas((ls) => [...ls, { itemId, quantidade: n, rotulo: `${item.nome} · ${n} ${item.unidadeMedida ?? 'un'}` }])
    setQuantidade('')
  }

  const criar = async () => {
    setBusy(true)
    try {
      const { data } = await createEstoqueTransferencia({
        localOrigemId: origemId, localDestinoId: destinoId,
        itens: linhas.map((l) => (l.unidadeId != null ? { itemId: l.itemId, unidadeId: l.unidadeId } : { itemId: l.itemId, quantidade: l.quantidade })),
        motivo: motivo.trim() || null,
        previsaoChegada: previsao || null,
        requisicaoId,
      })
      toast.success(`Transferência ${data?.data?.numero ?? ''} criada`)
      navigate(`/interno/estoque/transferencias/${data?.data?.id}`)
    } catch (err) {
      const e = tratarErro(err)
      if (e.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return }
      toast.error(e.texto)
    } finally {
      setBusy(false)
    }
  }

  if (!podeTransferir) {
    return (
      <>
        <PageTitle>Nova transferência</PageTitle>
        <Card><div style={{ padding: 24, color: BD.grayText }}>
          Criar transferência exige a permissão "Transferir entre praças". <Button $variant="secondary" onClick={() => navigate('/interno/estoque/transferencias')}>Voltar</Button>
        </div></Card>
      </>
    )
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <PageTitle>Nova transferência</PageTitle>
          <PageSubtitle>
            Comece pelo item para ver em que praça ele está livre. Uma das duas praças precisa ser sua: pela origem, você oferece e o destino aprova; pelo destino, você pede e a origem, dona do equipamento, aprova. Nada sai agora: a transferência nasce solicitada e passa por aprovação.
          </PageSubtitle>
        </div>
        <Button $variant="secondary" onClick={() => navigate('/interno/estoque/transferencias')}>‹ Transferências</Button>
      </div>

      {carregandoLocais ? <Card><div style={{ textAlign: 'center', padding: 24 }}><Spinner /></div></Card>
        : erroLocais ? (
          <Card><div style={{ padding: 24, color: BD.red }}>
            Não foi possível carregar as praças. <Button $variant="secondary" onClick={() => recarregarLocais()}>Tentar de novo</Button>
          </div></Card>
        ) : (
          <>
            {requisicaoId != null && (
              <Card style={{ padding: 12, marginBottom: 16, fontSize: 13 }}>
                Para o pedido <b>{pedido?.numero ?? `#${requisicaoId}`}</b>: o destino é a praça do pedido e só entram itens dele.
              </Card>
            )}
            <Card style={{ padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                <Field>
                  <label>De (praça de origem)</label>
                  <Select value={origemId ?? ''} onChange={(e) => escolherOrigem(Number(e.target.value) || null)}>
                    <option value="">— escolha —</option>
                    {locais.filter((l) => l.id !== destinoId).map((l) => <option key={l.id} value={l.id}>{l.nome}{l.noEscopo ? '' : ' (outra praça)'}</option>)}
                  </Select>
                </Field>
                <Field>
                  <label>Para (praça de destino)</label>
                  <Select value={destinoId ?? ''} disabled={requisicaoId != null} onChange={(e) => setDestinoId(Number(e.target.value) || null)}>
                    <option value="">— escolha —</option>
                    {locais.filter((l) => l.id !== origemId).map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
                  </Select>
                </Field>
                <Field><label>Previsão de chegada</label><Input type="date" min={hoje} value={previsao} onChange={(e) => setPrevisao(e.target.value)} /></Field>
                <Field><label>Motivo (opcional)</label><Input value={motivo} maxLength={400} onChange={(e) => setMotivo(e.target.value)} /></Field>
              </div>
              {avisoDoLado && <div style={{ marginTop: 10, fontSize: 13, color: BD.grayText }}>{avisoDoLado}</div>}
            </Card>

            {origemPendente && (
              <Card style={{ padding: 16, marginBottom: 16, borderColor: SEVERIDADE.atencao.borda }}>
                <div style={{ fontSize: 14, marginBottom: 12 }}>
                  Trocar a origem de <b>{nomeDoLocal(origemId)}</b> para <b>{nomeDoLocal(origemPendente.id)}</b> tira {plural(linhas.length, 'linha já incluída', 'linhas já incluídas')}: o que foi escolhido é da origem atual.
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Button onClick={() => aplicarEscolha(confirmarOrigem(escolha))}>Trocar e limpar</Button>
                  <Button $variant="secondary" onClick={() => aplicarEscolha(manterOrigem(escolha))}>Manter {nomeDoLocal(origemId)}</Button>
                </div>
              </Card>
            )}

            <Card style={{ padding: 16, marginBottom: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>O que vai</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, alignItems: 'end' }}>
                <Field>
                  <label>Item</label>
                  <Select value={itemId ?? ''} onChange={(e) => { setBuscaPeca(''); setItemId(Number(e.target.value) || null) }}>
                    <option value="">— escolha o item —</option>
                    {itensOferecidos.map((i) => <option key={i.id} value={i.id}>{i.nome} ({i.controle === 'SERIAL' ? 'peça' : 'quantidade'})</option>)}
                  </Select>
                </Field>
              </div>

              {item && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Onde tem {item.nome} livre</div>
                  {ondeTemQ.isLoading ? <Spinner />
                    : ondeTemQ.isError ? (
                      <div style={{ fontSize: 13, color: BD.red }}>
                        Não foi possível consultar onde tem. <Button $variant="secondary" onClick={() => ondeTemQ.mutate()}>Tentar de novo</Button>
                      </div>
                    ) : !ondeTem.length ? (
                      <div style={{ fontSize: 13, color: BD.grayText }}>Nenhuma praça tem este item livre agora.</div>
                    ) : (
                      <div style={{ display: 'grid', gap: 6 }}>
                        {ondeTem.map((l) => (
                          <div key={l.localId} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '6px 10px', borderRadius: 8, border: `1px solid ${l.ehOrigem ? BD.black : BD.grayBorder}` }}>
                            <span style={{ flexGrow: 1, fontSize: 13 }}><b>{l.localNome}</b> · {l.texto}</span>
                            {l.ehOrigem ? <span style={{ fontSize: 12, color: BD.grayText }}>origem escolhida</span>
                              : l.podeSerOrigem ? <Button $variant="secondary" onClick={() => escolherOrigem(l.localId)}>Usar como origem</Button>
                                : <span style={{ fontSize: 12, color: BD.grayText }}>{l.ehDestino ? 'é o destino' : ''}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              )}

              {item && origemId == null && (
                <div style={{ marginTop: 12, fontSize: 13, color: BD.grayText }}>Escolha a origem, numa praça da lista ou no campo "De", para incluir {ehSerial ? 'as peças' : 'a quantidade'}.</div>
              )}

              {origemId != null && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, alignItems: 'end', marginTop: 12 }}>
                    {item && !ehSerial && (
                      <>
                        <Field>
                          <label>Quantidade{item.unidadeMedida ? ` (${item.unidadeMedida})` : ''}</label>
                          <Input type="number" min={1} value={quantidade} onChange={(e) => setQuantidade(e.target.value)} />
                          <div style={{ fontSize: 12, color: ondeTemQ.isError ? BD.red : BD.grayText, marginTop: 4 }}>
                            {ondeTemQ.isLoading ? 'consultando o livre na origem…'
                              : ondeTemQ.isError ? 'não foi possível consultar o livre na origem'
                                : `livre na origem: ${livre}${jaIncluido > 0 ? ` (${jaIncluido} já nesta transferência)` : ''}`}
                          </div>
                        </Field>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Button $variant="secondary" disabled={busy || !!impQuantidade} onClick={incluirQuantidade}>Incluir</Button>
                          {impQuantidade && quantidade.trim() !== '' && <span style={{ fontSize: 13, color: BD.grayText }}>{impQuantidade}</span>}
                        </div>
                      </>
                    )}
                  </div>

                  {item && ehSerial && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 13, color: BD.grayText, marginBottom: 6 }}>
                        Peças disponíveis na origem. Só aparecem as que estão em estoque, OK e fora de outra transferência aberta.
                      </div>
                      <SeletorDePecas
                        consulta={pecasQ}
                        busca={buscaPeca}
                        mostrarPatrimonio
                        aoBuscar={setBuscaPeca}
                        escolhidas={jaEscolhidas as number[]}
                        aoEscolher={(u) => incluirPeca(u.id, u.serial)}
                        vazio={<div style={{ fontSize: 13, color: BD.grayText }}>Nenhuma peça deste item disponível na origem.</div>}
                      />
                    </div>
                  )}
                </>
              )}

              <div style={{ marginTop: 16 }}>
                <Table>
                  <thead><tr><th>Item</th><th /></tr></thead>
                  <tbody>
                    {linhas.length === 0 && <tr><td colSpan={2} style={{ color: BD.grayText, padding: 12 }}>Nada incluído ainda.</td></tr>}
                    {linhas.map((l, k) => (
                      <tr key={`${l.itemId}-${l.unidadeId ?? 'q'}-${k}`}>
                        <td>{l.rotulo}</td>
                        <td style={{ textAlign: 'right' }}>
                          <Button $variant="secondary" onClick={() => setLinhas((ls) => ls.filter((_, i) => i !== k))}>Remover</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card>

            <SoDesktop>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <Button disabled={busy || !!impedimento} onClick={criar}>Criar transferência</Button>
                {impedimento && <span style={{ fontSize: 13, color: BD.grayText }}>{impedimento}</span>}
              </div>
            </SoDesktop>
            <BarraFixa>
              {impedimento && <span style={{ fontSize: 12, color: BD.grayText }}>{impedimento}</span>}
              <Button disabled={busy || !!impedimento} onClick={criar}>Criar transferência</Button>
            </BarraFixa>
          </>
        )}
    </>
  )
}

export default EstoqueNovaTransferencia
